import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteExecutiveSummary,
  getExecutiveSummary,
  uploadExecutiveSummary,
} from "apis/executive-summary-report.api";
import { ErrorCode } from "enums";
import styles from "./executive-summary-upload.module.scss";

const MAX_FILE_SIZE = 12 * 1024 * 1024;

const formatBytes = (bytes) => {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getUploadedAt = (report) => {
  const value = report?.uploadedAt;
  if (!value) return "Recently uploaded";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const ExecutiveSummaryUpload = ({ campaignId }) => {
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [frameError, setFrameError] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [toast, setToast] = useState("");
  const inputRef = useRef(null);
  const mountedRef = useRef(true);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(message);
    toastTimerRef.current = setTimeout(() => setToast(""), 4500);
  }, []);

  const loadReport = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");
    setFrameError(false);
    try {
      const data = await getExecutiveSummary({ campaignId });
      if (!mountedRef.current) return;
      setReport(data || null);
    } catch (error) {
      if (!mountedRef.current) return;
      setReport(null);
      setErrorMessage("The report couldn't be loaded. Try again.");
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    mountedRef.current = true;
    loadReport();
    return () => {
      mountedRef.current = false;
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, [campaignId, loadReport]);

  const validateFile = (file) => {
    if (!file || file.size === 0) {
      return "No file selected, or the file is empty.";
    }
    if (!/\.html?$/i.test(file.name)) {
      return "Only .html or .htm files are accepted.";
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File is ${formatBytes(file.size)}; the limit is ${formatBytes(MAX_FILE_SIZE)}.`;
    }
    return "";
  };

  const handleFile = async (file) => {
    const validationMessage = validateFile(file);
    if (validationMessage) {
      setErrorMessage(validationMessage);
      return;
    }

    setErrorMessage("");
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const data = await uploadExecutiveSummary({
        campaignId,
        file,
        onUploadProgress: (event) => {
          if (!event.total) return;
          setUploadProgress(Math.round((event.loaded / event.total) * 100));
        },
      });
      setReport(data || null);
      setFrameError(false);
      showToast("Executive summary embedded");
    } catch (error) {
      const errorCode = error?.data?.errorCode;
      const messages = {
        [ErrorCode.ExecutiveSummaryFileMissing]: "No file / empty file.",
        [ErrorCode.ExecutiveSummaryFileNotHtml]: "Only .html or .htm files are accepted.",
        [ErrorCode.ExecutiveSummaryUploadFailed]:
          "The upload couldn't be stored. Your current report is unchanged.",
      };
      setErrorMessage(
        error?.data?.message ||
          messages[errorCode] ||
          "The upload failed. Your current report is unchanged.",
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (event) => {
    const [file] = event.target.files || [];
    if (file) handleFile(file);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const [file] = event.dataTransfer.files || [];
    if (file) handleFile(file);
  };

  const handleRemove = async () => {
    setIsRemoving(true);
    setErrorMessage("");
    try {
      await deleteExecutiveSummary({ campaignId });
      setReport(null);
      setIsRemoveOpen(false);
      setIsExpanded(false);
    } catch (error) {
      setErrorMessage(error?.data?.message || "The report could not be removed. Try again.");
    } finally {
      setIsRemoving(false);
    }
  };

  const handleFrameError = () => setFrameError(true);

  if (isLoading) {
    return (
      <div className={styles.state} role="status">
        Loading executive summary...
      </div>
    );
  }

  if (errorMessage && !report) {
    return (
      <div className={styles.state}>
        <div className={styles.errorIcon} aria-hidden="true">
          !
        </div>
        <div className={styles.stateTitle}>Unable to load executive summary</div>
        <div className={styles.stateText}>{errorMessage}</div>
        <button type="button" className={styles.secondaryButton} onClick={loadReport}>
          Retry
        </button>
      </div>
    );
  }

  const uploadOverlay = isUploading && (
    <div className={styles.uploadOverlay} role="status" aria-live="polite">
      <div className={styles.spinner} aria-hidden="true" />
      <div className={styles.uploadOverlayText}>Embedding report... {uploadProgress}%</div>
    </div>
  );

  if (!report) {
    return (
      <div
        className={`${styles.emptyState} ${isDragging ? styles.dropActive : ""}`}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".html,.htm,text/html"
          onChange={handleInputChange}
          hidden
        />
        <div className={styles.uploadIcon} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
            <path d="M12 16V4m0 0L8 8m4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 13v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5" strokeLinecap="round" />
          </svg>
        </div>
        <div className={styles.stateTitle}>Embed the executive summary</div>
        <div className={styles.stateText}>
          Upload the HTML report generated for this campaign. It will display here in full, exactly
          as authored, and can be updated or removed at any time.
        </div>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
        >
          <span aria-hidden="true">+</span> Choose HTML file
        </button>
        <div className={styles.hint}>
          Or drag the file here · .html/.htm up to {formatBytes(MAX_FILE_SIZE)}
        </div>
        {errorMessage && <div className={styles.inlineError}>{errorMessage}</div>}
        {uploadOverlay}
      </div>
    );
  }

  const reportFrame = (
    <iframe
      title={report.fileName || "Executive summary"}
      className={styles.reportFrame}
      src={report.contentUrl}
      sandbox="allow-scripts allow-popups"
      referrerPolicy="no-referrer"
      onError={handleFrameError}
    />
  );

  return (
    <>
      <div className={styles.reportShellWrap}>
        <div className={styles.reportShell}>
          <div className={styles.reportHeader}>
            <div className={styles.fileInfo}>
              <div className={styles.fileIcon} aria-hidden="true">
                ▱
              </div>
              <div>
                <div className={styles.fileName}>{report.fileName || "Executive summary.html"}</div>
                <div className={styles.fileMeta}>
                  {formatBytes(report.sizeBytes || 0)} · Uploaded {getUploadedAt(report)}
                </div>
              </div>
            </div>
            <div className={styles.reportActions}>
              <button
                type="button"
                className={styles.actionButton}
                onClick={() => inputRef.current?.click()}
                disabled={isUploading}
              >
                ↻ Update
              </button>
              <button
                type="button"
                className={styles.actionButton}
                onClick={() => setIsRemoveOpen(true)}
                disabled={isUploading}
              >
                □ Remove
              </button>
              <button
                type="button"
                className={styles.expandButton}
                onClick={() => setIsExpanded(true)}
                aria-label="Expand report"
              >
                ↗
              </button>
            </div>
          </div>
          {errorMessage && <div className={styles.inlineError}>{errorMessage}</div>}
          <div className={styles.frameContainer}>
            {frameError ? (
              <div className={styles.state}>
                <div className={styles.errorIcon} aria-hidden="true">
                  !
                </div>
                <div className={styles.stateTitle}>Report couldn't be loaded</div>
                <div className={styles.stateText}>
                  The preview link may have expired. Try again to fetch a fresh one.
                </div>
                <button type="button" className={styles.secondaryButton} onClick={loadReport}>
                  Retry
                </button>
              </div>
            ) : (
              reportFrame
            )}
          </div>
          {uploadOverlay}
        </div>
        {toast && (
          <div className={styles.toast} role="status" aria-live="polite">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {toast}
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".html,.htm,text/html"
        onChange={handleInputChange}
        hidden
      />
      {isExpanded && (
        <div className={styles.fullscreen} role="dialog" aria-modal="true">
          <div className={styles.fullscreenHeader}>
            <strong>{report.fileName || "Executive summary.html"}</strong>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => setIsExpanded(false)}
            >
              ↙ Return
            </button>
          </div>
          {frameError ? (
            <div className={styles.state}>
              <div className={styles.errorIcon} aria-hidden="true">
                !
              </div>
              <div className={styles.stateTitle}>Report couldn't be loaded</div>
              <div className={styles.stateText}>
                The preview link may have expired. Try again to fetch a fresh one.
              </div>
              <button type="button" className={styles.secondaryButton} onClick={loadReport}>
                Retry
              </button>
            </div>
          ) : (
            reportFrame
          )}
        </div>
      )}
      {isRemoveOpen && (
        <div
          className={styles.modalOverlay}
          role="presentation"
          onMouseDown={() => setIsRemoveOpen(false)}
        >
          <div
            className={styles.confirmModal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-summary-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <h2 id="remove-summary-title">Remove embedded summary?</h2>
            <p>
              The report will no longer appear in this tab. You can upload a new HTML file at any
              time.
            </p>
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => setIsRemoveOpen(false)}
                disabled={isRemoving}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.removeButton}
                onClick={handleRemove}
                disabled={isRemoving}
              >
                {isRemoving ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExecutiveSummaryUpload;
